import {
	ConfidenceLevel,
	type DataSourceDetail,
	DataSourceDetailDataSourceTypeEnum,
	DataSourceDetailSourceDescriptionEnum,
	type DataSources,
	DataSourcesDataSourceTypeEnum,
	type DraftDataSourceDetail,
	type DraftDataSources,
	type DraftEmissionRequestFormData,
	type DraftEmissionsAttachment,
	type DraftEmissionsValuesBreakdown,
	type DraftEmissionValueItem,
	type DraftEmissionValues,
	DraftEmissionValuesAllocationMethodUsedEnum,
	type DraftEmissionValuesLocation,
	type DraftMethodologies,
	type DraftMethodologyStandards,
	type DraftVerificationAttachment,
	type DraftVerificationData,
	type EmissionRequestProductFormData,
	type EmissionsAttachment,
	type EmissionsValuesBreakdown,
	EmissionType,
	EmissionValueDataUsed,
	EmissionValueGranularityLevel,
	type EmissionValueItem,
	type EmissionValues,
	type EmissionValuesLocation,
	EmissionValuesLocationCountryEnum,
	EmissionValuesLocationLocationTypeEnum,
	EmissionValuesLocationRegionEnum,
	EmissionValueUnit,
	InitiativeLevel,
	LifecycleStage,
	ManufacturingLocationCountryEnum,
	ManufacturingLocationLocationTypeEnum,
	ManufacturingLocationRegionEnum,
	type Methodologies,
	MethodologiesImpactAssessmentMethodEnum,
	type MethodologyStandards,
	MethodologyStandardsStandardCompliantEnum,
	Standard,
	type VerificationAttachment,
	type VerificationData,
	VerificationProvider,
} from '@api-client/api'
import { omit } from 'lodash-es'

// Function overloads for type safety
function convertToEnum<T>(
	value: string | undefined | null,
	enumType: Record<string, T>,
	name: string,
	returnUndefinedIfInvalid: true
): T | undefined
function convertToEnum<T>(
	value: string,
	enumType: Record<string, T>,
	name: string,
	returnUndefinedIfInvalid?: false
): T
function convertToEnum<T>(
	value: string | undefined | null,
	enumType: Record<string, T>,
	name: string,
	returnUndefinedIfInvalid = false
): T | undefined {
	if (!value || !Object.values(enumType).includes(value as T)) {
		if (returnUndefinedIfInvalid) {
			return undefined
		}
		throw new Error(`${value} is not a valid ${name}`)
	}
	return value as T
}

const convertDraftToDataSourcesDetails = (
	draftData: DraftDataSourceDetail | DataSourceDetail,
	fallbackDataSourceType?: DataSourceDetailDataSourceTypeEnum
): DataSourceDetail => {
	const {
		dataSourceType: rawDataSourceType,
		sourceDescription,
		...others
	} = draftData
	const dataSourceType = rawDataSourceType ?? fallbackDataSourceType
	if (!dataSourceType) {
		throw new Error('The type of a data source (primary/secondary) is not set')
	}
	if (!sourceDescription) {
		throw new Error('The description of a data source is not set')
	}
	return {
		dataSourceType: convertToEnum(
			dataSourceType,
			DataSourceDetailDataSourceTypeEnum,
			'data source type'
		),
		sourceDescription: convertToEnum(
			sourceDescription,
			DataSourceDetailSourceDescriptionEnum,
			'data source description'
		),
		...others,
	}
}

const convertDraftToDataSources = (
	draftData?: DraftDataSources | DataSources
): DataSources => {
	if (!draftData) {
		throw new Error('Missing data sources')
	}
	const { dataSourceDetails, dataSourceType, ...others } = draftData
	if (!dataSourceType) {
		throw new Error('No data source type defined')
	}
	return {
		dataSourceType: convertToEnum(
			dataSourceType,
			DataSourcesDataSourceTypeEnum,
			'data source type'
		),
		dataSourceDetails: (dataSourceDetails ?? []).map(item =>
			convertDraftToDataSourcesDetails(
				item,
				DataSourceDetailDataSourceTypeEnum.PRIMARY
			)
		),
		...others,
	}
}

const convertDraftToMethodologyStandards = (
	draftData?: DraftMethodologyStandards | MethodologyStandards
): MethodologyStandards => {
	if (!draftData) {
		throw new Error('Missing methodology standards')
	}
	const { standardCompliant, standardUsed, ...others } = draftData
	const finalStandardCompliant =
		standardCompliant ??
		(standardUsed && standardUsed !== Standard.OTHER
			? MethodologyStandardsStandardCompliantEnum.YES
			: MethodologyStandardsStandardCompliantEnum.UNKNOWN)
	return {
		standardCompliant: convertToEnum(
			finalStandardCompliant,
			MethodologyStandardsStandardCompliantEnum,
			'methodology standard compliance'
		),
		standardUsed:
			standardUsed &&
			convertToEnum(standardUsed, Standard, 'methodology standard'),
		...others,
	}
}

const convertToUnixTimestamp = (
	value: string | number | undefined | null
): number | undefined => {
	if (value === undefined || value === null) {
		return undefined
	}
	if (typeof value === 'number') {
		return value
	}
	const ms = new Date(value).getTime()
	return Number.isNaN(ms) ? undefined : Math.floor(ms / 1000)
}

const convertDraftToMethodologies = (
	draftData?: DraftMethodologies | Methodologies
): Methodologies => {
	if (!draftData) {
		return {
			methodologyStandards: {
				standardCompliant: MethodologyStandardsStandardCompliantEnum.UNKNOWN,
			},
		}
	}
	const {
		methodologyStandards,
		impactAssessmentMethod,
		collectionStartDate,
		collectionEndDate,
		validityStartDate,
		validityEndDate,
		...others
	} = draftData
	return {
		methodologyStandards:
			convertDraftToMethodologyStandards(methodologyStandards),
		impactAssessmentMethod:
			impactAssessmentMethod &&
			convertToEnum(
				impactAssessmentMethod,
				MethodologiesImpactAssessmentMethodEnum,
				'impact assessment method'
			),
		collectionStartDate,
		collectionEndDate,
		validityStartDate,
		validityEndDate,
		...others,
	}
}

const convertDraftToEmissionValuesLocation = (
	draftData?: DraftEmissionValuesLocation | EmissionValuesLocation
): EmissionValuesLocation | undefined => {
	if (!draftData) {
		return undefined
	}
	const { country, locationType, region, manufacturingLocation, ...others } =
		draftData

	// Default to GLOBAL if location type is missing to prevent blocking users
	const finalLocationType =
		locationType || EmissionValuesLocationLocationTypeEnum.GLOBAL

	return {
		country:
			country &&
			convertToEnum(country, EmissionValuesLocationCountryEnum, 'country'),
		locationType: convertToEnum(
			finalLocationType,
			EmissionValuesLocationLocationTypeEnum,
			'location type'
		),
		region:
			region &&
			convertToEnum(region, EmissionValuesLocationRegionEnum, 'region'),
		manufacturingLocation: manufacturingLocation?.locationType && {
			country:
				manufacturingLocation.country &&
				convertToEnum(
					manufacturingLocation.country,
					ManufacturingLocationCountryEnum,
					'country'
				),
			locationType: convertToEnum(
				manufacturingLocation.locationType,
				ManufacturingLocationLocationTypeEnum,
				'location type'
			),
			region:
				manufacturingLocation.region &&
				convertToEnum(
					manufacturingLocation.region,
					ManufacturingLocationRegionEnum,
					'region'
				),
		},

		...others,
	}
}

const convertLifeCycleBreakdownItem = (
	name: string,
	draftData: DraftEmissionValueItem | EmissionValueItem
): EmissionValueItem => {
	const { emissionType, lifecycleStage, unit, value, ...others } = draftData
	if (value === undefined) {
		throw new Error(`Value is not set for ${name} breakdown`)
	}
	if (!unit) {
		throw new Error(`Unit is not set for ${name} breakdown`)
	}
	return {
		lifecycleStage:
			lifecycleStage &&
			convertToEnum(
				lifecycleStage,
				LifecycleStage,
				`lifecycle stage for ${name} breakdown`
			),
		emissionType:
			emissionType &&
			convertToEnum(
				emissionType,
				EmissionType,
				`emission type in ${name} breakdown`
			),
		unit: convertToEnum(unit, EmissionValueUnit, `unit in ${name} breakdown`),
		value,
		...others,
	}
}

const convertLifeCycleBreakdown = (
	name: string,
	draftData?: readonly EmissionValueItem[] | readonly DraftEmissionValueItem[]
): readonly EmissionValueItem[] => {
	if (!draftData?.length) {
		return []
	}
	// Filter out items with undefined values before converting
	return draftData
		.filter(item => item.value !== undefined)
		.map(item => convertLifeCycleBreakdownItem(name, item))
}

const convertDraftToEmissionValuesBreakdown = (
	draftData?: DraftEmissionsValuesBreakdown | EmissionsValuesBreakdown
): EmissionsValuesBreakdown | undefined => {
	if (!draftData) {
		return undefined
	}
	const {
		materialAcquisitionAndPreprocessing,
		production,
		distributionAndStorage,
		productUse,
		endOfLife,
		dataUsed,
		unit,
		...others
	} = draftData
	return {
		materialAcquisitionAndPreprocessing: convertLifeCycleBreakdown(
			'materialAcquisitionAndPreprocessing',
			materialAcquisitionAndPreprocessing
		),
		production: convertLifeCycleBreakdown('production', production),
		distributionAndStorage: convertLifeCycleBreakdown(
			'distributionAndStorage',
			distributionAndStorage
		),
		productUse: convertLifeCycleBreakdown('productUse', productUse),
		endOfLife: convertLifeCycleBreakdown('endOfLife', endOfLife),
		dataUsed:
			dataUsed &&
			convertToEnum(dataUsed, EmissionValueDataUsed, 'data-used value'),
		unit: unit && convertToEnum(unit, EmissionValueUnit, 'unit'),
		...others,
	}
}

const convertDraftToEmissionValues = (
	draftData?: DraftEmissionValues | EmissionValues
): EmissionValues => {
	if (!draftData) {
		throw new Error('Missing emission values')
	}
	const {
		confidence,
		emissionValuesGranularity,
		emissionValuesLocation,
		emissionsValuesBreakdown,
		emissionsAttachments,
		allocationMethodUsed,
		...others
	} = draftData

	// Auto-detect granularity if missing to prevent blocking users
	let finalGranularity = emissionValuesGranularity
	if (!finalGranularity) {
		const breakdown = emissionsValuesBreakdown
		const mat = breakdown?.materialAcquisitionAndPreprocessing
		const prod = breakdown?.production
		const dist = breakdown?.distributionAndStorage

		// Check if detailed lifecycle mode (multiple items per stage)
		const isDetailedMode =
			(mat?.length ?? 0) > 1 ||
			(prod?.length ?? 0) > 1 ||
			(dist?.length ?? 0) > 1

		if (isDetailedMode) {
			finalGranularity = EmissionValueGranularityLevel.DETAILED_LIFECYCLE
		} else {
			// Check if simple lifecycle breakdown is filled (all three stages have values)
			const hasBreakdown =
				mat?.[0]?.value !== undefined &&
				prod?.[0]?.value !== undefined &&
				dist?.[0]?.value !== undefined

			finalGranularity = hasBreakdown
				? EmissionValueGranularityLevel.LIFECYCLE
				: EmissionValueGranularityLevel.PRODUCT
		}
	}

	return {
		confidence:
			confidence &&
			convertToEnum(confidence, ConfidenceLevel, 'confidence level'),
		emissionValuesGranularity: convertToEnum(
			finalGranularity,
			EmissionValueGranularityLevel,
			'granularity'
		),
		emissionValuesLocation: convertDraftToEmissionValuesLocation(
			emissionValuesLocation
		),
		emissionsValuesBreakdown: convertDraftToEmissionValuesBreakdown(
			emissionsValuesBreakdown
		),
		emissionsAttachments: emissionsAttachments?.map(
			convertDraftToEmissionsAttachment
		),
		allocationMethodUsed: convertToEnum(
			allocationMethodUsed,
			DraftEmissionValuesAllocationMethodUsedEnum,
			'allocation method',
			true
		),
		...others,
	}
}

const convertDraftToVerificationAttachment = (
	draftData: VerificationAttachment | DraftVerificationAttachment
): VerificationAttachment => {
	const { id, name, ...other } = draftData
	if (!id) {
		throw new Error('id is missing in a verification file')
	}
	if (!name) {
		throw new Error('name is missing in a verification file')
	}
	return { id, name, ...other }
}

const convertDraftToEmissionsAttachment = (
	draftData: EmissionsAttachment | DraftEmissionsAttachment
): EmissionsAttachment => {
	const { id, name, ...other } = draftData
	if (!id) {
		throw new Error('id is missing in a emissions attachement file')
	}
	if (!name) {
		throw new Error('name is missing in a emissions attachement file')
	}
	return { id, name, ...other }
}

const convertDraftToVerificationData = (
	draftData?: VerificationData | DraftVerificationData
): VerificationData | undefined => {
	if (!draftData) {
		return draftData
	}
	const {
		isDataVerified,
		verificationAttachments,
		verificationProvider,
		verificationStandard,
		...others
	} = draftData
	if (isDataVerified === undefined) {
		return undefined
	}
	return {
		isDataVerified,
		verificationAttachments: verificationAttachments?.map(
			convertDraftToVerificationAttachment
		),
		verificationProvider:
			verificationProvider &&
			convertToEnum(
				verificationProvider,
				VerificationProvider,
				'verification provider'
			),
		verificationStandard:
			verificationStandard &&
			convertToEnum(verificationStandard, Standard, 'verification standard'),
		...others,
	}
}

const convertUnixTimestampToIso = (
	value: string | number | undefined | null
): string | undefined => {
	if (value === undefined || value === null) {
		return undefined
	}
	if (typeof value === 'string') {
		return value
	}
	return new Date(value * 1000).toISOString()
}

// Normalizes the breakdown stage from either a flat number (custom form)
// or an array of items (PACT form) into the array format expected by the backend.
const normalizeBreakdownStage = (
	stage: unknown,
	unit: EmissionValueUnit | undefined
): readonly DraftEmissionValueItem[] => {
	if (Array.isArray(stage)) {
		return stage as DraftEmissionValueItem[]
	}
	if (typeof stage === 'number') {
		return [{ value: stage, unit }]
	}
	return []
}

const KNOWN_VERIFICATION_PROVIDERS = new Set<string>(
	Object.values(VerificationProvider)
)

// Converts custom form data structural differences into the standard
// DraftEmissionRequestFormData format expected by convertDraftToEmissionRequest.
//
// The custom form (JSON Forms) differs from the PACT form in these ways:
// 1. dataSources: uses a separate `secondarySourceDetails` array instead of
//    per-item dataSourceType in a single `dataSourceDetails` array.
// 2. emissionsValuesBreakdown: lifecycle stages are flat numbers instead of
//    arrays of { unit, value } objects.
// 3. verificationData.verificationProvider: may be a free-text string not in
//    the enum — normalized to OTHER + additionalVerificationProviderDetails.
export const normalizeCustomFormData = (
	data: DraftEmissionRequestFormData
): DraftEmissionRequestFormData => {
	const {
		dataSources,
		emissionValues,
		verificationData,
		methodologies,
		answeredUnit,
	} = data
	const unit = answeredUnit as unknown as EmissionValueUnit | undefined

	// Normalize dataSources: merge secondarySourceDetails into dataSourceDetails
	type WithSecondary = typeof dataSources & {
		secondarySourceDetails?: DraftDataSourceDetail[]
	}
	const rawDataSources = dataSources as WithSecondary
	const normalizedDataSources: DraftDataSources | undefined = rawDataSources
		? (() => {
				const { secondarySourceDetails, dataSourceDetails, ...rest } =
					rawDataSources
				if (!secondarySourceDetails?.length) {
					return rawDataSources
				}
				const primaryItems = (dataSourceDetails ?? []).map(item =>
					Object.assign({}, item, {
						dataSourceType: DataSourceDetailDataSourceTypeEnum.PRIMARY,
					})
				)
				const secondaryItems = secondarySourceDetails.map(item =>
					Object.assign({}, item, {
						dataSourceType: DataSourceDetailDataSourceTypeEnum.SECONDARY,
					})
				)
				return {
					...rest,
					dataSourceDetails: [...primaryItems, ...secondaryItems],
				}
			})()
		: undefined

	// Normalize emissionsValuesBreakdown: flat numbers → arrays of { unit, value }
	const breakdown = emissionValues?.emissionsValuesBreakdown as
		| Record<string, unknown>
		| undefined
	const normalizedEmissionValues: DraftEmissionValues | undefined =
		emissionValues && breakdown
			? {
					...emissionValues,
					emissionsValuesBreakdown: {
						...breakdown,
						materialAcquisitionAndPreprocessing: normalizeBreakdownStage(
							breakdown.materialAcquisitionAndPreprocessing,
							unit
						),
						production: normalizeBreakdownStage(breakdown.production, unit),
						distributionAndStorage: normalizeBreakdownStage(
							breakdown.distributionAndStorage,
							unit
						),
						productUse: normalizeBreakdownStage(breakdown.productUse, unit),
						endOfLife: normalizeBreakdownStage(breakdown.endOfLife, unit),
					} as DraftEmissionsValuesBreakdown,
				}
			: emissionValues

	// Normalize methodologies: ISO strings → Unix timestamps, infer standardCompliant
	const rawStandards = methodologies?.methodologyStandards
	const normalizedMethodologyStandards: DraftMethodologyStandards | undefined =
		rawStandards
			? {
					...rawStandards,
					standardCompliant:
						rawStandards.standardCompliant ??
						(rawStandards.standardUsed &&
						rawStandards.standardUsed !== Standard.OTHER
							? MethodologyStandardsStandardCompliantEnum.YES
							: MethodologyStandardsStandardCompliantEnum.UNKNOWN),
				}
			: rawStandards
	const normalizedMethodologies: DraftMethodologies | undefined = methodologies
		? {
				...methodologies,
				methodologyStandards: normalizedMethodologyStandards,
				collectionStartDate: convertToUnixTimestamp(
					methodologies.collectionStartDate
				) as unknown as string,
				collectionEndDate: convertToUnixTimestamp(
					methodologies.collectionEndDate
				) as unknown as string,
				validityStartDate: convertToUnixTimestamp(
					methodologies.validityStartDate
				) as unknown as string,
				validityEndDate: convertToUnixTimestamp(
					methodologies.validityEndDate
				) as unknown as string,
			}
		: undefined

	// Normalize verificationProvider: if free-text value not in enum, use OTHER
	// and move the original value to additionalVerificationProviderDetails.
	const rawProvider = verificationData?.verificationProvider as
		| string
		| undefined
	const normalizedVerificationData: DraftVerificationData | undefined =
		verificationData &&
		rawProvider &&
		!KNOWN_VERIFICATION_PROVIDERS.has(rawProvider)
			? {
					...verificationData,
					verificationProvider: VerificationProvider.OTHER,
					additionalVerificationProviderDetails: rawProvider,
				}
			: verificationData

	return {
		...data,
		dataSources: normalizedDataSources,
		emissionValues: normalizedEmissionValues,
		methodologies: normalizedMethodologies,
		verificationData: normalizedVerificationData,
	}
}

const denormalizeBreakdownStage = (stage: unknown): number | undefined => {
	if (typeof stage === 'number') {
		return stage
	}
	if (!Array.isArray(stage) || stage.length === 0) {
		return undefined
	}
	return (stage[0] as DraftEmissionValueItem).value
}

// Reverses normalizeCustomFormData so the JSON Forms survey receives its expected format.
//
// The inverse of each normalization step:
// 1. dataSources: split dataSourceDetails back into primary dataSourceDetails
//    and a separate secondarySourceDetails array (without dataSourceType per item).
// 2. emissionsValuesBreakdown: arrays of { unit, value } → flat numbers (first item's value).
// 3. methodologies dates: Unix timestamps → ISO strings.
// 4. verificationData.verificationProvider: OTHER + additionalVerificationProviderDetails
//    → restored free-text value.
export const denormalizeCustomFormData = (
	data: DraftEmissionRequestFormData
): DraftEmissionRequestFormData => {
	const { dataSources, emissionValues, verificationData, methodologies } = data

	// Denormalize dataSources: split dataSourceDetails into primary + secondarySourceDetails
	type WithSecondary = typeof dataSources & {
		secondarySourceDetails?: Omit<DraftDataSourceDetail, 'dataSourceType'>[]
	}
	const denormalizedDataSources: WithSecondary | undefined = dataSources
		? (() => {
				const { dataSourceDetails, ...rest } = dataSources
				const hasTypedItems = dataSourceDetails?.some(
					item => item.dataSourceType !== undefined
				)
				if (!hasTypedItems) {
					return dataSources as WithSecondary
				}
				const primaryItems = dataSourceDetails
					?.filter(
						item =>
							item.dataSourceType === DataSourceDetailDataSourceTypeEnum.PRIMARY
					)
					.map(item => omit(item, 'dataSourceType'))
				const secondaryItems = dataSourceDetails
					?.filter(
						item =>
							item.dataSourceType ===
							DataSourceDetailDataSourceTypeEnum.SECONDARY
					)
					.map(item => omit(item, 'dataSourceType'))
				return {
					...rest,
					dataSourceDetails: primaryItems,
					...(secondaryItems?.length
						? { secondarySourceDetails: secondaryItems }
						: {}),
				}
			})()
		: undefined

	// Denormalize emissionsValuesBreakdown: arrays of { unit, value } → flat numbers
	const breakdown = emissionValues?.emissionsValuesBreakdown as
		| Record<string, unknown>
		| undefined
	const denormalizedEmissionValues: DraftEmissionValues | undefined =
		emissionValues && breakdown
			? {
					...emissionValues,
					emissionsValuesBreakdown: {
						...breakdown,
						materialAcquisitionAndPreprocessing: denormalizeBreakdownStage(
							breakdown.materialAcquisitionAndPreprocessing
						),
						production: denormalizeBreakdownStage(breakdown.production),
						distributionAndStorage: denormalizeBreakdownStage(
							breakdown.distributionAndStorage
						),
						productUse: denormalizeBreakdownStage(breakdown.productUse),
						endOfLife: denormalizeBreakdownStage(breakdown.endOfLife),
					} as unknown as DraftEmissionsValuesBreakdown,
				}
			: emissionValues

	// Denormalize methodologies: Unix timestamps → ISO strings, strip inferred standardCompliant
	const rawMethodologyStandards = methodologies?.methodologyStandards
	const denormalizedMethodologyStandards:
		| DraftMethodologyStandards
		| undefined = rawMethodologyStandards
		? (omit(
				rawMethodologyStandards,
				'standardCompliant'
			) as DraftMethodologyStandards)
		: rawMethodologyStandards
	const denormalizedMethodologies: DraftMethodologies | undefined =
		methodologies
			? {
					...methodologies,
					methodologyStandards: denormalizedMethodologyStandards,
					collectionStartDate: convertUnixTimestampToIso(
						methodologies.collectionStartDate as unknown as number
					) as unknown as string,
					collectionEndDate: convertUnixTimestampToIso(
						methodologies.collectionEndDate as unknown as number
					) as unknown as string,
					validityStartDate: convertUnixTimestampToIso(
						methodologies.validityStartDate as unknown as number
					) as unknown as string,
					validityEndDate: convertUnixTimestampToIso(
						methodologies.validityEndDate as unknown as number
					) as unknown as string,
				}
			: undefined

	// Denormalize verificationProvider: OTHER + additionalVerificationProviderDetails → free text
	const denormalizedVerificationData: DraftVerificationData | undefined =
		verificationData?.verificationProvider === VerificationProvider.OTHER &&
		verificationData.additionalVerificationProviderDetails
			? {
					...verificationData,
					verificationProvider:
						verificationData.additionalVerificationProviderDetails as unknown as VerificationProvider,
					additionalVerificationProviderDetails: undefined,
				}
			: verificationData

	return {
		...data,
		dataSources: denormalizedDataSources as DraftDataSources,
		emissionValues: denormalizedEmissionValues,
		methodologies: denormalizedMethodologies,
		verificationData: denormalizedVerificationData,
	}
}

export const convertDraftToEmissionRequest = (
	draftData: DraftEmissionRequestFormData | EmissionRequestProductFormData
): EmissionRequestProductFormData => {
	const {
		dataSources,
		emissionValues,
		methodologies,
		verificationData,
		reductionInitiatives,
		...others
	} = normalizeCustomFormData(draftData)
	return {
		dataSources: convertDraftToDataSources(dataSources),
		emissionValues: convertDraftToEmissionValues(emissionValues),
		methodologies: convertDraftToMethodologies(methodologies),
		verificationData: convertDraftToVerificationData(verificationData),
		reductionInitiatives: reductionInitiatives?.map(initiative =>
			Object.assign({}, initiative, {
				initiativeLevel: InitiativeLevel.PRODUCT,
			})
		),
		...others,
	}
}
